import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { LayoutGrid, Table2, Plus, UserCog, Download, Trash2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader, StatusPill, FieldValue, Pager, Empty, BRANCH_LABEL, SortTh, urlSort } from "../components/common";
import { Staff } from "../components/StaffAvatar";
import { LeadForm, Sel } from "../components/LeadForm";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { CallButton } from "../components/CallButton";

const inp = "bg-muted/60 border h-9 px-2 text-sm outline-none focus:border-primary";
const TEXT_SORTS = ["company_name", "contact_name", "source", "status", "owner", "branch"];
const BRANCH_OPTS = [{ v: "DEL", t: "Delhi (FBD)" }, { v: "MUM", t: "Mumbai" }, { v: "UNASSIGNED", t: "Unassigned" }];

const LeadCard = ({ l, selectable, checked, toggle, onChanged }) => {
  const nav = useNavigate();
  return (
    <div className="ax-panel p-3 flex gap-3 items-start" data-testid={`lead-card-${l.lead_id}`}>
      {selectable && <input type="checkbox" className="mt-1 h-5 w-5 shrink-0" data-testid={`lead-card-select-${l.lead_id}`} checked={checked} onChange={() => toggle(l.lead_id)} />}
      <button onClick={() => nav(`/sales/leads/${l.lead_id}`)} className="flex-1 min-w-0 text-left" data-testid={`lead-card-open-${l.lead_id}`}>
        <div className="flex items-center gap-2"><span className="font-mono text-[11px] text-primary">{l.lead_id}</span><StatusPill code={l.status} /></div>
        <div className="font-medium truncate mt-0.5">{l.company_name || l.contact_name || "—"}</div>
        <div className="text-xs text-muted-foreground truncate">{l.contact_name || ""}{l.contact_name ? " · " : ""}<FieldValue row={l} f="phone" /></div>
        <div className="flex items-center justify-between gap-2 mt-1.5 text-xs"><Staff name={l.owner} size={18} />
          <span className={l.overdue ? "text-rose-500" : "text-muted-foreground"}>{l.next_contact_date ? `${l.overdue ? "Overdue · " : "Next "}${l.next_contact_date}` : (l.no_next_action ? "No next step" : "")}</span></div>
      </button>
      <CallButton l={l} testid={`lead-card-call-${l.lead_id}`} onDone={onChanged} />
    </div>
  );
};

export const LeadTable = ({ items, selected, setSelected, selectable, onChanged, sort }) => {
  const nav = useNavigate();
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  if (!items.length) return <Empty text="No leads match these filters." />;
  return (
    <>
    <div className="md:hidden space-y-2" data-testid="leads-cards">
      {items.map((l) => <LeadCard key={l.lead_id} l={l} selectable={selectable} checked={selected.includes(l.lead_id)} toggle={toggle} onChanged={onChanged} />)}
    </div>
    <div className="ax-panel ax-scroll ax-freeze no-select hidden md:block" data-testid="leads-table">
      <table className="ax-table w-full min-w-[1100px]">
        <thead><tr>{[["lead_id", "Lead ID"], ["source_date", "Date"], ["company_name", "Company"], ["contact_name", "Contact"], [null, "Phone"], ["source", "Source"], ["status", "Status"],
          ["owner", "Owner"], ["branch", "Branch"], ["last_contact_date", "Last contact"], ["next_contact_date", "Next contact"]].map(([k, l]) => <SortTh key={l} k={k} label={l} sort={sort} />)}</tr></thead>
        <tbody>{items.map((l) => (
          <tr key={l.lead_id} className="cursor-pointer" onClick={() => nav(`/sales/leads/${l.lead_id}`)} data-testid={`lead-row-${l.lead_id}`}>
            <td className="font-mono text-xs text-primary whitespace-nowrap">
              {selectable && <input type="checkbox" className="mr-2 align-middle" data-testid={`lead-select-${l.lead_id}`} checked={selected.includes(l.lead_id)} onClick={(e) => e.stopPropagation()} onChange={() => toggle(l.lead_id)} />}
              {l.lead_id}{l.dup_status === "review" && <span className="ml-1 text-amber-400" title="Possible duplicate">●</span>}</td>
            <td className="num text-xs">{l.source_date}</td>
            <td className="max-w-[220px] truncate">{l.company_name || "—"}</td>
            <td className="max-w-[160px] truncate">{l.contact_name || "—"}</td>
            <td className="text-xs"><FieldValue row={l} f="phone" /></td>
            <td className="text-xs">{l.source}</td>
            <td><StatusPill code={l.status} /></td>
            <td className="whitespace-nowrap"><Staff name={l.owner} size={20} /></td>
            <td className="text-xs">{BRANCH_LABEL[l.branch] || l.branch}</td>
            <td className="num text-xs">{l.last_contact_date || "—"}</td>
            <td className={`num text-xs ${l.overdue ? "text-rose-400" : ""}`}>{l.next_contact_date || (l.no_next_action ? <span className="text-amber-400">none</span> : "—")}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
    </>
  );
};

const Kanban = ({ cols }) => {
  const nav = useNavigate();
  return (
    <div className="overflow-x-auto pb-2 no-select" data-testid="leads-kanban">
      <div className="grid grid-cols-7 gap-3 min-w-[1400px]">
        {cols.map((c) => (
          <div key={c.code} className="ax-panel" data-testid={`kanban-col-${c.code}`}>
            <div className="p-3 border-b flex justify-between items-center"><StatusPill code={c.code} label={c.label} /><span className="num text-xs text-muted-foreground">{c.total}</span></div>
            <div className="p-2 space-y-2 max-h-[65vh] overflow-y-auto">
              {c.items.map((l) => (
                <button key={l.lead_id} onClick={() => nav(`/sales/leads/${l.lead_id}`)} data-testid={`kanban-card-${l.lead_id}`}
                  className="w-full text-left border bg-background/60 p-2 hover:border-primary transition-colors">
                  <div className="text-xs font-mono text-primary">{l.lead_id}</div>
                  <div className="text-sm truncate">{l.company_name || l.contact_name}</div>
                  <div className="flex justify-between text-[11px] text-muted-foreground mt-1"><Staff name={l.owner} size={16} /><span className={l.overdue ? "text-rose-400" : ""}>{l.next_contact_date || ""}</span></div>
                </button>
              ))}
              {c.total > c.items.length && <div className="text-[11px] text-center text-muted-foreground py-1">+{c.total - c.items.length} more · use table view</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ReassignDialog = ({ ids, staff, onClose, onDone }) => {
  const [owner, setOwner] = useState("");
  const [reason, setReason] = useState("");
  const submit = () => api.post("/leads/bulk-reassign", { lead_ids: ids, new_owner: owner, reason })
    .then(() => { toast.success("Reassignment sent for approval"); onDone(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card" data-testid="reassign-dialog">
        <DialogHeader><DialogTitle className="font-display">Bulk reassign {ids.length} leads</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">A manager or founder must approve this before ownership changes. You can't approve your own request.</p>
        <Sel testid="reassign-owner" value={owner} onChange={setOwner} opts={staff} blank="Select new owner" />
        <textarea data-testid="reassign-reason" placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} className={`${inp} h-20 py-2 w-full`} />
        <button data-testid="reassign-submit" disabled={!owner || !reason} onClick={submit} className="bg-primary text-primary-foreground h-9 text-sm disabled:opacity-50">Request approval</button>
      </DialogContent>
    </Dialog>
  );
};

const DeleteDialog = ({ ids, onClose, onDone }) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = () => {
    setBusy(true);
    api.post("/leads/bulk-delete", { lead_ids: ids, reason }).then((r) => {
      if (r.data.deleted.length) toast.success(`${r.data.deleted.length} lead(s) moved to the Deleted bin`);
      r.data.blocked.forEach((b) => toast.error(`${b.lead_id}: ${b.reason}`));
      onDone();
    }).catch((e) => toast.error(errMsg(e))).finally(() => setBusy(false));
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card" data-testid="bulk-delete-dialog">
        <DialogHeader><DialogTitle className="font-display">Delete {ids.length} lead(s)?</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">They move to the Deleted bin and disappear from lists, reports and callbacks. A founder can restore them within 30 days. Leads that already have a UID are skipped.</p>
        <textarea data-testid="bulk-delete-reason" placeholder="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} className={`${inp} h-20 py-2 w-full`} />
        <button data-testid="bulk-delete-confirm" disabled={busy} onClick={submit} className="bg-rose-600 text-white h-9 text-sm disabled:opacity-50">{busy ? "Deleting…" : "Delete"}</button>
      </DialogContent>
    </Dialog>
  );
};

export default function Leads() {
  const { can } = useAuth();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [cfg, setCfg] = useState(null);
  const [staff, setStaff] = useState([]);
  const [heads, setHeads] = useState([]);
  const [former, setFormer] = useState([]);
  const ownerOpts = useMemo(() => [...staff, ...former.map((n) => ({ v: n, t: `${n} (former)` }))], [staff, former]);
  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: 50 });
  const [cols, setCols] = useState([]);
  const [selected, setSelected] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showRe, setShowRe] = useState(false);
  const [showDel, setShowDel] = useState(false);
  const view = sp.get("view") || "table";
  const params = Object.fromEntries([...sp.entries()].filter(([k, v]) => v && k !== "view"));
  const key = sp.toString();
  const set = (k, v) => { const n = new URLSearchParams(sp); v ? n.set(k, v) : n.delete(k); if (k !== "page") n.delete("page"); setSp(n); };
  const load = useCallback(() => {
    const p = Object.fromEntries([...new URLSearchParams(key).entries()].filter(([k, v]) => v && k !== "view"));
    if (p.stale) p.stale = true;
    if ((new URLSearchParams(key).get("view") || "table") === "kanban") api.get("/leads/kanban", { params: p }).then((r) => setCols(r.data)).catch((e) => toast.error(errMsg(e)));
    else api.get("/leads", { params: p }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [key]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSelected([]); }, [key]);
  useEffect(() => {
    api.get("/sales/config").then((r) => setCfg(r.data)).catch(() => {});
    api.get("/staff").then((r) => setStaff(r.data)).catch(() => {});
    api.get("/staff/heads").then((r) => setHeads(r.data)).catch(() => {});
    api.get("/staff/former").then((r) => setFormer(r.data)).catch(() => {});
  }, []);
  return (
    <div>
      <PageHeader eyebrow="Sales & Leads" title="Leads" desc="Every lead from your Sales Tracker, plus new ones added here or through approved intake forms.">
        <div className="flex border">
          <button data-testid="view-table" onClick={() => set("view", "")} className={`h-9 px-3 ${view === "table" ? "bg-muted text-primary" : ""}`}><Table2 className="h-4 w-4" /></button>
          <button data-testid="view-kanban" onClick={() => set("view", "kanban")} className={`h-9 px-3 ${view === "kanban" ? "bg-muted text-primary" : ""}`}><LayoutGrid className="h-4 w-4" /></button>
        </div>
        {can("sales", "export") && <button data-testid="request-export-button" onClick={() => nav(`/sales/exports?${new URLSearchParams(params).toString()}`)} className="border h-9 px-3 text-sm flex items-center gap-2 hover:border-primary"><Download className="h-4 w-4" /> Request export</button>}
        {can("sales", "assign") && selected.length > 0 && <button data-testid="bulk-reassign-button" onClick={() => setShowRe(true)} className="border border-primary h-9 px-3 text-sm flex items-center gap-2"><UserCog className="h-4 w-4" /> Reassign {selected.length}</button>}
        {can("sales", "delete") && selected.length > 0 && <button data-testid="bulk-delete-button" onClick={() => setShowDel(true)} className="border border-rose-400 text-rose-600 h-9 px-3 text-sm flex items-center gap-2"><Trash2 className="h-4 w-4" /> Delete {selected.length}</button>}
        {can("sales", "create") && <button data-testid="new-lead-button" onClick={() => setShowForm(true)} className="bg-primary text-primary-foreground h-9 px-4 text-sm flex items-center gap-2"><Plus className="h-4 w-4" /> New lead</button>}
      </PageHeader>
      <div className="ax-panel p-3 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 mb-4" data-testid="lead-filters">
        <input data-testid="lead-filter-q" defaultValue={params.q || ""} onKeyDown={(e) => e.key === "Enter" && set("q", e.target.value)} placeholder="Search & press Enter" className={`${inp} col-span-2`} />
        {view === "table" && <Sel testid="lead-filter-status" value={params.status} onChange={(v) => set("status", v)} blank="All statuses" opts={(cfg?.statuses || []).map((s) => ({ v: s.code, t: s.label }))} />}
        {heads.length > 0 && <Sel testid="lead-filter-head" value={params.head} onChange={(v) => set("head", v)} blank="All teams" opts={heads.map((h) => ({ v: h.user_id, t: `${h.name}'s team (${h.team.length})` }))} />}
        <Sel testid="lead-filter-owner" value={params.owner} onChange={(v) => set("owner", v)} blank="All owners" opts={ownerOpts} />
        <Sel testid="lead-filter-branch" value={params.branch} onChange={(v) => set("branch", v)} blank="All branches" opts={BRANCH_OPTS} />
        {view === "table" && <Sel testid="lead-filter-source" value={params.source} onChange={(v) => set("source", v)} blank="All sources" opts={cfg?.sources || []} />}
      </div>
      {view === "kanban" ? <Kanban cols={cols} /> : (
        <>
          <LeadTable items={data.items} selected={selected} setSelected={setSelected} selectable={can("sales", "assign") || can("sales", "delete")} onChanged={load} sort={urlSort(sp, setSp, "source_date", TEXT_SORTS)} />
          <Pager page={data.page} total={data.total} size={data.page_size} onPage={(p) => set("page", String(p))} />
        </>
      )}
      <LeadForm open={showForm} onClose={() => setShowForm(false)} onCreated={(l) => nav(`/sales/leads/${l.lead_id}`)} />
      {showRe && <ReassignDialog ids={selected} staff={staff} onClose={() => setShowRe(false)} onDone={() => { setShowRe(false); setSelected([]); }} />}
      {showDel && <DeleteDialog ids={selected} onClose={() => setShowDel(false)} onDone={() => { setShowDel(false); setSelected([]); load(); }} />}
    </div>
  );
}
